/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Modal, Spin, Input, message, Form,
} from 'antd';
import Table from 'components/Table';
import { CheckCircleOutlined } from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitControls';

import {
  getAdministracionPlazasDetalles,
  patchAdministracionPlazaDetalle,
} from 'api/administracionPlazasDetalles';
import { getUsuarios } from 'api/usuarios/usuarios';
import { getUsuariosLimitaciones } from 'api/usuarios/usuariosLimitaciones';
import { getEstadosGlobales } from 'api/catalogos';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import store from 'store/store';
import { selectUser } from 'store/auth';
import moment from 'moment';

const { TextArea } = Input;

const AutorizacionPlazaDetalleModal = ({
  open,
  onClose,
  empleadoNombre,
  idAdministracionPlaza,
  onSuccessAutorizacion,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  // Catálogos auxiliares
  const [usuarios, setUsuarios] = useState([]);
  const [limitaciones, setLimitaciones] = useState([]);
  const [estados, setEstados] = useState([]);

  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [recordToAuthorize, setRecordToAuthorize] = useState(null);
  const [formAutorizar] = Form.useForm();

  // Estado para saber qué permisos tengo YO
  const [misLimitacionesIds, setMisLimitacionesIds] = useState([]);

  //  CARGA DE DATOS
  useEffect(() => {
    let mounted = true;
    const loadData = async () => {
      setLoading(true);
      try {
        // 1. Cargamos catálogos necesarios
        const [userData, limData, estData] = await Promise.all([
          getUsuarios(),
          getUsuariosLimitaciones(),
          getEstadosGlobales(),
        ]);

        // Identificar al usuario logueado
        const currentUserId = selectUser(store.getState())?.id;

        const allLimitaciones = limData.results || limData;

        // Filtramos: ¿Qué IDs de limitación (niveles) me pertenecen a mí?
        const misPermisos = allLimitaciones
          .filter((l) => l.usuario === currentUserId)
          .map((l) => l.id);

        if (mounted) {
          setUsuarios(userData);
          setLimitaciones(allLimitaciones);
          setEstados(estData);
          setMisLimitacionesIds(misPermisos);
        }

        // 2. Cargamos las firmas filtradas por la plaza
        if (idAdministracionPlaza) {
          const response = await getAdministracionPlazasDetalles({
            administracion_plaza: idAdministracionPlaza,
          });
          if (mounted) setData(response.results || response || []);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Autorización de Plaza Detalle] error al cargar datos de firmas', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (open) {
      loadData();
    } else {
      setData([]); // Limpiar al cerrar
    }
    return () => { mounted = false; };
  }, [open, idAdministracionPlaza]);

  // --- HELPER: Obtener nombre de usuario ---
  const getNombreUsuario = (limitacionId) => {
    if (!limitacionId) return 'N/A';
    const limitacion = limitaciones.find((l) => l.id === limitacionId);
    if (!limitacion) return `Nivel ID: ${limitacionId}`;

    const usuarioReal = usuarios.find((u) => u.id === limitacion.usuario);
    return usuarioReal
      ? `${usuarioReal.first_name} ${usuarioReal.last_name}`
      : usuarioReal?.email || 'Usuario sin nombre';
  };

  // --- HELPER: Obtener descripción estado ---
  const getEstadoDescripcion = (val) => {
    const id = typeof val === 'object' ? val.id : val;
    const estado = estados.find((e) => e.id === id);
    return estado ? estado.descripcion : 'Desconocido';
  };

  // --- ACCIONES ---
  const handlePreAutorizar = (record) => {
    setRecordToAuthorize(record);
    formAutorizar.resetFields();
    formAutorizar.setFieldsValue({ observaciones: record.observaciones });
    setVisibleAutorizar(true);
  };

  const onAutorizarFinish = async () => {
    if (!recordToAuthorize) return;
    try {
      setLoading(true);
      await formAutorizar.validateFields();
      const values = formAutorizar.getFieldsValue();
      const payload = {
        observaciones: values.observaciones,
        estados_globales: ESTATUS_AUTORIZADO,
      };

      const success = await patchAdministracionPlazaDetalle(recordToAuthorize.id, payload);

      if (success) {
        message.success('Firma aplicada correctamente');
        setVisibleAutorizar(false);
        setRecordToAuthorize(null);

        // eslint-disable-next-line max-len
        const response = await getAdministracionPlazasDetalles({ administracion_plaza: idAdministracionPlaza });
        setData(response.results || response || []);

        if (onSuccessAutorizacion) onSuccessAutorizacion();
      }
    } catch (error) {
      // No se reporta como error de guardado si son campos de validación
      // (antd ya los muestra); de lo contrario dejamos rastro para diagnóstico.
      if (!error?.errorFields) {
        // eslint-disable-next-line no-console
        console.error('[Autorización de Plaza Detalle] error al autorizar la firma', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'Usuario Requerido',
      dataIndex: 'usuario_limitacion_de_informacion',
      key: 'usuario',
      width: 200,
      render: (val) => getNombreUsuario(val),
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 250,
      render: (val) => val || '-',
    },
    {
      titleText: 'Fecha Firma',
      dataIndex: 'fecha_de_creacion',
      key: 'fecha_de_creacion',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      title: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => {
        const estado = estados.find((e) => e.id === val);
        return estado ? estado.descripcion : 'Pendiente';
      },
    },
  ];

  return (
    <>
      <Modal
        title={`Firmas para: ${empleadoNombre || ''}`}
        open={open}
        onCancel={onClose}
        width={900}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={loading}>
          <Table
            cols={columns}
            data={data}

          />
        </Spin>
      </Modal>

      <Modal
        open={visibleAutorizar}
        title="Confirmar Firma"
        footer={null}
        closable={false}
        width={500}
      >
        <Form form={formAutorizar} layout="vertical" onFinish={onAutorizarFinish}>
          <p>
            <strong>Firmando como: </strong>
            {recordToAuthorize
            && getNombreUsuario(recordToAuthorize.usuario_limitacion_de_informacion)}
          </p>
          <Form.Item name="observaciones" label="Observaciones" rules={[{ required: true, message: 'Requerido' }]}>
            <TextArea rows={3} placeholder="Ej. Autorizado correctamente" />
          </Form.Item>
          <FormSubmitControls
            onFinish={() => formAutorizar.submit()}
            onCancel={() => setVisibleAutorizar(false)}
            loading={loading}
            okText="Autorizar"
          />
        </Form>
      </Modal>
    </>
  );
};

export default AutorizacionPlazaDetalleModal;
